// Renders a Project data record (see project-data.js) into project detail markup.
// One data record + its images = one page — no hand-built HTML per project.
//
// A project's body is `story`: a flat ordered array of blocks (image, images,
// act, beforeAfter, interlude) rendered top to bottom. Consecutive image/images
// blocks are batched into a single .gallery section so the existing wide/split
// rhythm keeps working; an act, slider, or interlude flushes that batch and
// renders itself. This is the one system both full documentation (AT
// Residence) and sparse-asset projects (GS Residence) run through.

// Shopify note: no subfolders in theme assets — every project image was
// flattened+renamed to project-{slug}-{base}-{width}.jpg at migration time
// (shopify-export/asset-rename-map.tsv in the original project) and is
// served from window.SHOPIFY_ASSET_BASE (the CDN prefix, set once in
// theme.liquid) rather than a path relative to the current page's depth —
// that relative-depth distinction no longer applies, every asset resolves
// from the same absolute prefix regardless of which page references it.
function imagesBasePath(slug) {
  return `${window.SHOPIFY_ASSET_BASE}project-${slug}-`;
}

function imgTag(slug, image, { className = "", loading = "lazy", fetchpriority = "" } = {}) {
  const base = imagesBasePath(slug);
  const widths = image.widths;
  const largest = widths[widths.length - 1];
  const srcset = widths.map((w) => `${base}${image.base}-${w}.jpg ${w}w`).join(",\n            ");
  const fp = fetchpriority ? ` fetchpriority="${fetchpriority}"` : "";
  return `<img class="${className}"
      src="${base}${image.base}-${largest}.jpg"
      srcset="${srcset}"
      sizes="${image.sizes}"
      loading="${loading}"${fp}
      alt="${image.alt}">`;
}

function renderBeforeAfter(slug, block, { eager = false } = {}) {
  const style = block.style ? ` style="${block.style}"` : "";
  const loadOpts = eager ? { loading: "eager", fetchpriority: "high" } : {};
  // block.className: "ba--lead" for a project's own leading slider
  // (homepage-style full-bleed) / "ba--enlarged" for a secondary one
  // that just needs to be bigger than the cramped old default — see
  // their shared comment in main.css.
  const extraClass = block.className ? ` ${block.className}` : "";
  return `
<section class="reveal-block">
  <div class="reveal-head">
    <h2 class="reveal-el">${block.heading}</h2>
    <span class="hint reveal-el">${block.hint}</span>
  </div>
  <div class="ba reveal-el${extraClass}" id="${block.id}"${style}>
    ${imgTag(slug, block.after, { className: "after", ...loadOpts })}
    ${imgTag(slug, block.before, { className: "before" })}
    <div class="before-veil"></div>
    <span class="ba-label l-before">Before</span>
    <span class="ba-label l-after">After</span>
    <div class="ba-handle"><div class="ba-grip"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6"><path d="M9 7l-5 5 5 5M15 7l5 5-5 5"/></svg></div></div>
  </div>
</section>`;
}

function renderInterlude(block) {
  return `
<section class="interlude">
  <p class="q reveal-el">${block.quote}</p>
  <div class="attr reveal-el">${block.attribution}</div>
</section>`;
}

// num = this act's 1-based position among the page's own acts (not the
// story array index — acts are the only block type that gets numbered,
// see renderStory's own counter). Added 2026-08-21, per explicit report
// that standalone act moments (never paired beside an image — every act
// is its own full-width section, confirmed live) read as a small,
// repetitive paragraph floating in an oversized empty field between two
// full-bleed photos. Same ghosted-numeral technique already established
// at .reflections::before (large, low-opacity, decorative) rather than a
// new motif — gives each act the same "designed, numbered beat" quality
// Process (01-04) and Selected Work (01-05) already have, so the empty
// space reads as intentional composition instead of an accidental gap.
function renderAct(block, num) {
  const paragraphs = Array.isArray(block.text) ? block.text : [block.text];
  return `
<section class="act reveal-el">
  <div class="act-num" aria-hidden="true">${String(num).padStart(2, "0")}</div>
  <div class="act-label">${block.label}</div>
  <div class="act-text">${paragraphs.map((p) => `<p>${p}</p>`).join("\n    ")}</div>
</section>`;
}

// 'image' = one full-width image (the old "wide" treatment).
// 'images' = a run of 2+ images sharing a row (the old "split" treatment,
// generalized to whatever column count the run has). Never emits an <img>
// for a missing image, and drops the whole block if nothing's left.
function renderStoryImageBlock(slug, block, { eager = false } = {}) {
  const loadOpts = eager ? { loading: "eager", fetchpriority: "high" } : {};
  if (block.type === "image") {
    if (!block.image) return "";
    return `<div class="wide reveal-el">${imgTag(slug, block.image, loadOpts)}</div>`;
  }
  const images = (block.images || []).filter(Boolean);
  if (!images.length) return "";
  if (images.length === 1) {
    return `<div class="wide reveal-el">${imgTag(slug, images[0], loadOpts)}</div>`;
  }
  const cols = images.length === 2 ? "" : ` style="grid-template-columns:repeat(${images.length},1fr)"`;
  // Only the first frame (if this run happens to be the page's very first
  // story block) gets the eager treatment — the rest of a multi-image run
  // are genuinely below it, lazy is correct for them.
  const frames = images.map((img, i) => `<div class="frame reveal-el">${imgTag(slug, img, i === 0 ? loadOpts : {})}</div>`).join("\n    ");
  return `<div class="split"${cols}>\n    ${frames}\n  </div>`;
}

function renderStory(slug, story) {
  const sections = [];
  let galleryBuffer = [];
  let actNum = 0;

  const flushGallery = () => {
    if (!galleryBuffer.length) return;
    sections.push(`\n<section class="gallery">\n  ${galleryBuffer.join("\n  ")}\n</section>`);
    galleryBuffer = [];
  };

  // The story's very first block sits directly under the hero/stats — near
  // enough to the top on every project page that lazy-loading it just adds
  // a native fetch-trigger delay on top of reveal.js's own scroll-in
  // trigger, for content that's essentially guaranteed to be needed within
  // the first scroll or two. Per explicit request ("images at the top can
  // also lazy load out"), only this first block gets eager/high-priority
  // loading — everything after it stays lazy, genuinely deferred content.
  (story || []).forEach((block, i) => {
    const eager = i === 0;
    if (block.type === "image" || block.type === "images") {
      const html = renderStoryImageBlock(slug, block, { eager });
      if (html) galleryBuffer.push(html);
      return;
    }
    flushGallery();
    if (block.type === "act") sections.push(renderAct(block, ++actNum));
    else if (block.type === "beforeAfter") sections.push(renderBeforeAfter(slug, block, { eager }));
    else if (block.type === "interlude") sections.push(renderInterlude(block));
    else throw new Error(`Unknown story block type: ${block.type}`);
  });

  flushGallery();
  return sections.join("\n");
}

// A subtitle line for a related-card: a project's own kicker if it has one,
// otherwise location plus whatever date info it gave (date or year).
function relatedSubtitle(project) {
  if (project.kicker) return project.kicker;
  const when = project.date || project.year;
  return when ? `${project.location} · ${when}` : project.location;
}

// Shared "editorial featured/supporting" tile row — same component as the
// homepage's own Selected Work gallery (homepage.js renderSelectedWork/
// initSelectedWorkHybrid), reused here for every "more work" moment on a
// project or service page. Not imported from homepage.js directly: that
// file hardcodes root-relative image paths (correct only from the site
// root), while every caller of this file sits one level deeper — this
// version reuses imgTag/imagesBasePath's already-correct "../../" path
// instead. Replaces the old drag-scroll rel-card carousel entirely (client
// request: reuse the homepage's own editorial gallery here instead of a
// second, different "browse more work" mechanic on every page).
// excludeSlug leaves a project off its own related row; service pages
// (not themselves a project) pass "" so nothing is excluded.
function renderRelatedWork(excludeSlug) {
  if (typeof PROJECTS === "undefined") return "";
  const projects = Object.values(PROJECTS).filter((p) => p.slug !== excludeSlug).slice(0, 5);
  if (!projects.length) return "";

  const num = (i) => String(i + 1).padStart(2, "0");
  const tileHtml = (project, i) => `
      <a class="work-tile${i === 0 ? " is-featured" : ""}" href="/pages/${project.slug}">
        ${imgTag(project.slug, project.cardImage || project.heroImage)}
        <div class="work-tile-info">
          <span class="work-tile-num">${num(i)}</span>
          <h3 class="work-tile-title">${project.title}</h3>
          <span class="work-tile-loc">${relatedSubtitle(project)}</span>
        </div>
      </a>`;
  const tilesHtml = projects.map(tileHtml).join("");

  return `
<section class="related">
  <div class="section-label reveal-el">More From The Studio</div>
  <div class="work-hybrid reveal-el" id="work-hybrid">${tilesHtml}</div>
  <a class="work-view-all" href="/pages/work">View All Projects &rarr;</a>
  <div class="related-cta-fade" id="related-cta-fade" aria-hidden="true"></div>
</section>`;
}

// Identical logic to homepage.js's initSelectedWorkHybrid — hover (fine-
// pointer devices) or click promotes a tile to featured; clicking the
// already-featured tile navigates normally. Kept as its own copy rather
// than a shared call for the same reason as renderRelatedWork above (no
// module system, and homepage.js isn't loaded on these pages).
function initRelatedWork(root) {
  const hybrid = root.querySelector("#work-hybrid");
  if (!hybrid) return;
  const tiles = Array.from(hybrid.querySelectorAll(".work-tile"));
  const canHover = window.matchMedia("(hover: hover)").matches;

  function setFeatured(tile) {
    tiles.forEach((t) => t.classList.toggle("is-featured", t === tile));
  }

  tiles.forEach((tile) => {
    tile.addEventListener("click", (e) => {
      if (!tile.classList.contains("is-featured")) {
        e.preventDefault();
        setFeatured(tile);
      }
    });
    if (canHover) tile.addEventListener("mouseenter", () => setFeatured(tile));
  });
}

// ---- Related -> CTA wash — same scroll-scrubbed fade technique as the
// homepage's Process -> Meliza / Reflections -> CTA washes (motion.js),
// reimplemented here since project pages don't load motion.js. Requires
// bare GSAP + ScrollTrigger (see the <script> tags added to every
// /work/{slug}/ page for this). Fades #related-cta-fade (a .related-cta-fade
// div, last child of .related) from 0->1 opacity as the section's bottom
// edge approaches the viewport top, so the drop into the CTA band reads as
// a gradual reveal instead of a hard light->dark cut.
// Pre-Shopify final-polish pass (2026-08-19), real bug fixed per explicit
// report: was triggered off `.related`'s OWN bottom edge (`start:"bottom
// bottom", end:"bottom top"` — the same recipe Process→Meliza and
// Reflections→CTA use, see PROJECT_STATUS.md), which starts darkening the
// instant the section (including "View All Projects", sitting right at
// its bottom) first becomes fully visible — so the entire time a visitor
// could actually read/click that link, the page was already actively
// getting darker under it. Unlike those other two fades, this section's
// own bottom edge IS a link the visitor still needs, not just trailing
// copy — reusing the same recipe here was fixing the wrong section's
// motion to the wrong anchor. Re-anchored to the footer's `.cta-band`
// ("Tell us your vision") instead: the fade now only starts once that
// next section actually begins entering from below, by which point
// `.related` (View All Projects included) has already fully scrolled
// past. `.cta-band` lives in the page's static <footer>, outside `root`
// (root is just #project, what renderProject fills) — queried from
// `document`, not `root`, for that reason.
function initRelatedToCtaFade(root) {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  if (reducedMotion || !window.gsap || !window.ScrollTrigger) return;
  const fade = root.querySelector("#related-cta-fade");
  const ctaBand = document.querySelector(".cta-band");
  if (!fade || !ctaBand) return;
  gsap.registerPlugin(ScrollTrigger);
  gsap.to(fade, {
    opacity: 1,
    ease: "none",
    // end offset (2026-08-21, per explicit user report the fade needs to
    // "activate sooner or extend longer" at the very bottom of project
    // pages): without it, "top top" means the fade only reaches full
    // opacity at the exact instant .cta-band's own top — the literal
    // seam — reaches the viewport top, zero settle time before the seam
    // itself is visible.
    //
    // Follow-up, real bug: a first attempt used a flat 240px offset,
    // verified working in this session's own small (~585px-tall)
    // automation window, but the user still saw the break on their real
    // monitor — checked the math afterward and 240px is only ~22-27% of
    // a real 900-1080px viewport vs. ~41% of the automation window's
    // own, so the "settle" buffer was proportionally much smaller than
    // it looked here. Switched to a percentage of the real viewport
    // height instead of a fixed px value, via a function (so it's
    // recalculated on resize, the standard ScrollTrigger pattern for
    // viewport-relative values) — 40% of innerHeight, comfortably
    // scaling on any real screen instead of being tuned to this one
    // small test window.
    scrollTrigger: {
      trigger: ctaBand,
      start: "top bottom",
      end: () => "top top+=" + Math.round(window.innerHeight * 0.4),
      scrub: true,
    },
  });
}

// "Make project images feel alive" (per explicit request, pre-Shopify
// final-polish pass, 2026-08-19) — same design intent as the homepage's
// Meliza portrait float (initMelizaPortraitFloat, motion.js: the whole
// portrait block scrub-lags behind scroll, scrub:2.2, yPercent:-7), but a
// different technique on purpose. Meliza's portrait sits in its own
// column with real margin around it, so moving the whole rigid block is
// safe. Story images here (.wide, inside .gallery, gap:1rem) sit edge-to-
// edge with the images above/below them — moving the whole block the same
// way would open a visible gap against a neighbor. Since .wide already has
// overflow:hidden (main.css), the correct version of this effect is
// instead: scale the <img> up so it overflows its own box, then let GSAP
// drift it within that overflow — the box itself never moves, only what's
// visible inside it, so neighboring images stay flush. Scoped to .wide
// only (full-width single-image beats — the "huge image" moments), not
// the smaller paired .frame images in .split rows, to keep the effect
// feeling special rather than applying it to every image on the page.
// Rebuilt (same session, follow-up) to match the real YODEZEEN mechanism
// the user reverse-engineered (reference demo + writeup supplied directly)
// rather than the scale-and-drift approximation this function started as.
// Real structure: the .wide CONTAINER is set ~29% taller than the image's
// own natural rendered height (not the image scaled up) — overflow:hidden
// clips that extra space into a "window" the image slides within. The
// image itself is never resized, just translated. Verified empirically
// (their own demo file, run locally in-browser and scrolled through) that
// this produces zero visible gaps at any scroll position before adopting
// the geometry here — the arithmetic looked like it should reveal a gap
// worked through by hand, but the reference implementation doesn't, so
// trusted the live behavior over the hand-derived algebra.
//
// Reimplemented with GSAP's ScrollTrigger instead of their vanilla
// requestAnimationFrame+getBoundingClientRect version — this site already
// loads GSAP/ScrollTrigger on every page for every other scroll effect
// (the before/after fades, the "What's Included" pin, etc.), so there's
// no dependency-weight argument for a second, parallel vanilla scroll
// system, and doing it via ScrollTrigger keeps this consistent with how
// every other scroll effect on the site is built. `scrub:true` (not a
// numeric lag like Meliza's own portrait float, scrub:2.2) — this effect
// is meant to track scroll directly with no catch-up delay, matching the
// reference's own frame-by-frame progress calc, which has no smoothing.
//
// heightDiff × (progress − 0.75)/(progress − 1) → travel range
// [-0.75×heightDiff, +0.25×heightDiff] is their exact "camera pans up"
// offset (you see the image's lower portion first, upper portion last) —
// reproduced here as a GSAP fromTo over the same start/end trigger points
// (top bottom → bottom top) rather than a hand-rolled progress fraction.
//
// Height must be set (and the page's real height must settle) BEFORE any
// OTHER ScrollTrigger reads document geometry, since making .wide taller
// shifts everything below it — see the call-order note where this is
// invoked in renderProject, and the trailing ScrollTrigger.refresh() once
// every image's real height is known (images load asynchronously, most
// are still lazy at this point in the page lifecycle, so heights aren't
// all knowable synchronously the moment this function runs).
function initStoryImageDrift(root) {
  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const isDesktop = window.matchMedia("(min-width: 821px)").matches;
  if (reducedMotion || !isDesktop || !window.gsap || !window.ScrollTrigger) return;
  gsap.registerPlugin(ScrollTrigger);
  // Follow-up (later session): dialed down from 1.29 (the reference's own
  // measured YODEZEEN value) after the user reported some gaps between
  // image sections reading as too long — root-caused as a real side
  // effect of this exact number: 1.29 means each .wide grows 29% taller
  // than its image's own natural height (confirmed live: +245px on a
  // single image at this viewport width), and with 2-3 .wide images
  // stacked only 1rem apart in a project page's own .gallery flow (unlike
  // YODEZEEN's own more spread-out layout), that adds up to real extra
  // scroll distance per page. 1.15 (matching FRAME_SCALE below, for one
  // consistent intensity across both effects) keeps the same motion, just
  // roughly half the added height.
  // Nudged 1.15->1.19 (2026-08-20, per explicit "barely more" request,
  // now that the drift is actually visible on .wide after the setup-
  // race fix above) — still well under the 1.29 value that caused the
  // "too long" complaint, so the added-height tradeoff stays small.
  const SCALE = 1.19;

  function setup(img) {
    const wide = img.closest(".wide");
    if (!wide || wide.dataset.driftReady) return;
    const imgHeight = img.offsetHeight;
    // Real bug (2026-08-20, user-reported "some images lost the drift
    // effect"): this used to stamp driftReady BEFORE checking imgHeight,
    // so an image that's already .complete the instant this runs (the
    // page's own first-block-eager image, or any image whose fetch just
    // happens to win the race against this script) but whose .wide
    // container hasn't finished its layout pass yet — offsetHeight still
    // 0 — got permanently marked ready and abandoned. The "load handler
    // below will retry" comment this replaced was only ever true for the
    // OTHER call path (an img with no load event left to fire, since
    // .complete was already true, has nothing to retry it). rAF instead
    // of a real retry cap: layout settles within a frame or two in every
    // real scenario, and driftReady is never set until setup genuinely
    // succeeds, so this can't spin forever on a truly-broken image.
    if (!imgHeight) { requestAnimationFrame(() => setup(img)); return; }
    wide.dataset.driftReady = "1";
    const outerHeight = imgHeight * SCALE;
    const heightDiff = outerHeight - imgHeight;
    wide.style.height = outerHeight + "px";
    gsap.fromTo(img,
      // Direction swapped (2026-08-21, per explicit user report: "the
      // images... are parallaxing down instead of up... makes it feel
      // like the scroll is working against the user"). Previously
      // FROM -0.75*diff TO +0.25*diff — measured live (AB Residence):
      // that put the image at y:-121.5px on entry and y:+40.5px on exit,
      // i.e. the visible "window" panned from the photo's lower content
      // up toward its upper content as the user scrolled down. Whatever
      // the textbook parallax-lag direction is, the user's own read of
      // the real result was that it felt backwards — trusted that over
      // re-deriving the math, same house convention this project already
      // follows elsewhere (verify live, don't just trust the algebra).
      // Swapped which endpoint is FROM vs TO — same excursion range,
      // reverse order — so the window now pans the opposite way.
      { y: heightDiff * 0.25 },
      // scrub:2.2 (2026-08-21, per explicit request referencing Meliza's
      // own portrait float, initMelizaPortraitFloat in motion.js) — was
      // scrub:true (instant, 1:1 scroll tracking, zero lag). scrub:true
      // vs a numeric scrub value are qualitatively different in GSAP, not
      // just a speed knob: a number is real time-based smoothing (seconds
      // to catch up to the scroll position), which is exactly what gives
      // Meliza's portrait its "floating, still catching up" feel — that
      // quality was structurally absent here regardless of SCALE/distance
      // tuning, which is why the effect read as "not there" even though
      // it was technically running and moving the image.
      { y: -heightDiff * 0.75, ease: "none", scrollTrigger: { trigger: wide, start: "top bottom", end: "bottom top", scrub: 2.2 } }
    );
    ScrollTrigger.refresh();
  }

  root.querySelectorAll(".wide img").forEach((img) => {
    if (img.complete && img.naturalWidth > 0) setup(img);
    else img.addEventListener("load", () => setup(img), { once: true });
  });

  // Image width (and so its natural rendered height, and so the whole
  // geometry above) is responsive — recompute on resize the same way the
  // reference's own onResize does, debounced to match this site's existing
  // resize-refresh convention (motion.js's ResizeObserver on <body>).
  let resizeTimer;
  window.addEventListener("resize", () => {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(() => {
      root.querySelectorAll(".wide").forEach((wide) => { wide.dataset.driftReady = ""; wide.style.height = ""; });
      root.querySelectorAll(".wide img").forEach((img) => setup(img));
    }, 200);
  });

  // Smaller paired images (.split .frame, the 2-3-across detail shots),
  // added per explicit follow-up request. Different technique on purpose:
  // .frame has a fixed aspect-ratio (main.css, aspect-ratio:3/4) and its
  // img is already object-fit:cover-filled to exactly match that box —
  // there's no "natural extra height" to grow the container into the way
  // .wide has, since the box size isn't image-driven here. So this uses
  // the simpler scale-and-drift-within-a-fixed-box version instead (what
  // the very first draft of this whole effect used, before the .wide
  // rebuild above) — no image measurement or load-waiting needed, since
  // the travel range is a fixed percentage of the element's own layout
  // height (GSAP's yPercent), independent of the image's real pixel size.
  // Smaller scale than .wide's 1.29 (these are inline detail pairs, not
  // "huge image" moments) but the same asymmetric 0.75/0.25 "camera pans
  // up" split, for one consistent feel across both effects.
  const FRAME_SCALE = 1.19; // kept matched to .wide's SCALE above
  const overflowPercent = (FRAME_SCALE - 1) * 100; // % of the frame's own height available to travel
  root.querySelectorAll(".split .frame img").forEach((img) => {
    gsap.set(img, { scale: FRAME_SCALE, transformOrigin: "center center" });
    gsap.fromTo(img,
      // Direction swapped, same fix/reasoning as the .wide drift above.
      { yPercent: overflowPercent * 0.25 },
      // scrub:2.2, same fix/reasoning as the .wide drift above.
      { yPercent: -overflowPercent * 0.75, ease: "none", scrollTrigger: { trigger: img.closest(".frame"), start: "top bottom", end: "bottom top", scrub: 2.2 } }
    );
  });
}

// Stats degrade gracefully: sparse-asset projects skip whatever fields
// they weren't given rather than rendering an empty "YEAR —" column. A
// project with `date` (e.g. "Nov 2023") shows it under "Date"; one with
// `year` shows it under "Year" — both feed the same slot, never both at
// once.
function renderStat(label, value) {
  if (value === undefined || value === null || value === "") return "";
  return `<div class="info-stat"><span class="info-stat-label">${label}</span><span class="info-stat-val">${value}</span></div>`;
}

function renderProject(project, root) {
  const slug = project.slug;

  // Project titles are centered over the hero (Build Pass 3, Part 5 —
  // client decision). Title + a small tracked LOCATION · YEAR line is the
  // whole overlay; the old kicker/subtitle lines are dropped in favor of
  // that one contrast (huge display type against tiny tracked caps).
  const heroYear = project.year || (project.date && (project.date.match(/\d{4}/) || [])[0]) || "";
  const heroMeta = [project.location, heroYear].filter(Boolean).join('<span class="hero-meta-rule"></span>');
  const hero = `
<header class="hero hero--project" id="hero">
  ${imgTag(slug, project.heroImage, { className: "hero-img", loading: "eager", fetchpriority: "high" })}
  <div class="hero-content">
    <h1 class="hero-title">${project.title}</h1>
    <div class="hero-meta-line">${heroMeta}</div>
  </div>
  <div class="scroll-cue">Scroll</div>
</header>`;

  const stats = [
    renderStat("Location", project.location),
    renderStat("Neighborhood", project.neighborhood),
    project.date ? renderStat("Date", project.date) : renderStat("Year", project.year),
    renderStat("Scope", project.scope)
  ].filter(Boolean).join("\n    ");

  // Horizontal stat strip (Variant 1 of 3 mocked up live for the user,
  // 2026-08-19 pre-Shopify final-polish pass — picked over a promoted-
  // tagline "editorial masthead" version and a narrowed-column two-color
  // rebalance). Replaces the old .info-meta/.info-body 1fr/1fr grid, which
  // put a full stacked stat column on the left against a right column that
  // only ever held this same one-line tagline — a large, viewport-
  // dependent dead gap on the right at any reasonable content length. One
  // full-width band instead: stats flow left-to-right with hairline
  // dividers between them, tagline sits as a caption underneath. The hero
  // title/location/year line above (`.hero-title`/`.hero-meta-line`,
  // unchanged) stays the section's real header — this strip is presented
  // as supporting detail below it, not a second title.
  // Follow-up (later session): tagline moved above the stat strip, per
  // explicit request — was strip-then-tagline, now tagline-then-strip.
  const info = `
<section class="info">
  <div class="info-tagline">${project.styleLine}</div>
  <div class="info-strip">
    ${stats}
  </div>
</section>`;

  const story = renderStory(slug, project.story);
  const related = renderRelatedWork(slug);

  root.innerHTML = hero + info + story + related;
  document.title = `${project.title} — Sixth Sense Studio`;
  // initStoryImageDrift runs first — it changes .wide's own height (see its
  // own comment), which shifts document geometry that initRelatedToCtaFade's
  // trigger reads. Wrong order would mean the CTA fade's start/end get
  // computed against a too-short page.
  initStoryImageDrift(root);
  initRelatedWork(root);
  initRelatedToCtaFade(root);
}
